import type { WixRicosEditorProps, RicosEditorType, RichContent } from '@wix/ricos';
import { WixRicosEditor, withEditorContext, ToolbarType } from '@wix/ricos';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import type { Ref } from 'react';
import React, { Component } from 'react';

import ActionButton from './action-button';
import {
  formattingToolbarSetting,
  getInlineToolbarSetting,
  mobileButtons,
} from './formatting-toolbar-setting';
import MobileFullScreenModal from './mobile-full-screen-modal';
import MobileHeader from './mobile-header';
import styles from './widget-styles.scss';

const footerToolbarConfig = {
  morePluginsMenu: {
    splitToSections: false,
    showSearch: false,
  },
};

type EditorProps = WixRicosEditorProps & {
  isAdmin: boolean;
  onPublish: (content: RichContent) => void;
  onCancel: () => void;
  editorEvents: {
    subscribe: () => {};
    unsubscribe: () => {};
    dispatch: (event: string) => Promise<any>;
    publish: () => RichContent;
  };
  selectedPlugins: Record<string, boolean>;
  useNewPluginMenu?: boolean;
  useFontSize?: boolean;
  useLinkToSection?: boolean;
  useVerticalSlimLayout?: boolean;
  useFileUploadSizeLimit?: boolean;
  isSSR: boolean;
};

class Editor extends Component<
  EditorProps,
  {
    isButtonDisabled: boolean;
    getSelectedPlugins:
      | ((
          selectedPlugins: Record<string, boolean>,
          additionalSettings: Record<string, any>
        ) => any[])
      | null;
  }
> {
  editor: Ref<RicosEditorType> = React.createRef();
  constructor(props: EditorProps) {
    super(props);
    if (!props.isSSR) {
      import(
        /*
      webpackChunkName: "editorPlugins"
       */ '../../../services/editor-plugins'
      ).then(module => {
        this.setState({
          getSelectedPlugins: module.getSelectedPlugins,
        });
      });
    }

    this.state = {
      isButtonDisabled: false,
      getSelectedPlugins: null,
    };
  }

  render() {
    const {
      locale,
      instance,
      theme,
      isMobile,
      content,
      onChange,
      toolbarSettings,
      isAdmin,
      onPublish,
      onCancel,
      selectedPlugins,
      useNewPluginMenu,
      useFontSize,
      useLinkToSection,
      useVerticalSlimLayout,
      wixExperiments = {},
      biSettings,
      useFileUploadSizeLimit,
      linkPanelSettings,
    } = this.props;

    const plugins =
      this.state.getSelectedPlugins?.(selectedPlugins, {
        useLinkToSection,
        useVerticalSlimLayout,
      }) ?? [];

    const getToolbarSettings = useNewPluginMenu
      ? () => [
          { name: ToolbarType.SIDE },
          {
            name: ToolbarType.MOBILE,
            getButtons: () => {
              return { mobile: { ios: mobileButtons, android: mobileButtons } };
            },
          },
          { name: ToolbarType.FOOTER, footerToolbarConfig },
          formattingToolbarSetting,
          getInlineToolbarSetting(useFontSize),
        ]
      : () => [];

    const props: WixRicosEditorProps = {
      locale,
      plugins,
      biSettings,
      theme,
      instance,
      isMobile,
      toolbarSettings: {
        ...toolbarSettings,
        getToolbarSettings,
      },
      placeholder: 'Add your content here...',
      content,
      onChange,
      onBusyChange: (isUploading: boolean) => this.setState({ isButtonDisabled: isUploading }),
      linkSettings: {
        anchorTarget: '_blank',
        relValue: 'noreferrer noopener',
      },
      linkPanelSettings,
      wixExperiments,
      fileUploadSizeLimit: useFileUploadSizeLimit ? 5 : undefined,
    };

    const remountkey = plugins.length;

    const publish = async () => {
      const publishedContent = await this.props.editorEvents.publish(); // editorEvents path
      // const content = await this.editor.current?.getContent('666', true); // deprecated path
      // const publishedContent = await this.editor.current?.publish(); // ref path
      onPublish(publishedContent);
    };

    const wixRicosEditor = <WixRicosEditor key={remountkey} ref={this.editor} {...props} />;

    return isMobile ? (
      <MobileFullScreenModal
        className="initialTransform"
        header={<MobileHeader onCancel={onCancel} onPublish={publish} />}
      >
        {wixRicosEditor}
      </MobileFullScreenModal>
    ) : (
      <>
        {wixRicosEditor}
        <div className={styles.actionBar}>
          <ActionButton
            text={isAdmin ? 'Publish' : 'View'}
            tooltipText={this.state.isButtonDisabled ? `Uploading files` : ''}
            onClick={publish}
            isDisabled={this.state.isButtonDisabled}
          />
        </div>
      </>
    );
  }
}

const withEnviroment =
  (ComponentToRender: any) => (props: Omit<EditorProps, 'isSSR' | 'editorEvents'>) => {
    const { isSSR } = useEnvironment();
    return <ComponentToRender {...props} isSSR={isSSR} />;
  };

export default withEnviroment(withEditorContext(Editor));
